import { useState } from 'react';
import { CheckCircle2, FileSearch, XCircle } from 'lucide-react';
import { getErrorMessage } from '../../lib/apiClient';
import { formatDate } from '../../utils/format';
import PublicShell from './components/PublicShell';
import { sha256OfFile, verifyFingerprint } from './signaturesApi';

/**
 * Public "verify this document" page. The browser computes the file's SHA-256
 * and only that fingerprint is sent, so the document is never uploaded. Any
 * change to a completed PDF, even one character, changes the fingerprint.
 */
export default function VerifyDocumentPage() {
  const [state, setState] = useState({ status: 'idle' });

  async function check(file) {
    if (!file) return;
    setState({ status: 'checking', name: file.name });
    try {
      const sha256 = await sha256OfFile(file);
      const result = await verifyFingerprint(sha256);
      setState({ status: result.match ? 'match' : 'nomatch', name: file.name, sha256, result });
    } catch (error) {
      setState({ status: 'error', name: file.name, message: getErrorMessage(error, 'Could not check that file') });
    }
  }

  return (
    <PublicShell title="Verify a signed document">
      <div className="mx-auto max-w-xl space-y-5 rounded-2xl border border-slate-200 bg-slate-100 p-8">
        <div className="text-center">
          <FileSearch className="mx-auto h-12 w-12 text-brand-600" aria-hidden="true" />
          <h2 className="mt-2 text-lg font-semibold text-slate-900">Check a document signed with MyDocUploads</h2>
          <p className="mt-1 text-sm text-slate-500">
            Choose the completed PDF. Your browser works out its fingerprint and checks it. The file itself is never
            uploaded.
          </p>
        </div>
        <label className="block">
          <span className="sr-only">Choose a PDF</span>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={(event) => check(event.target.files?.[0])}
            className="block w-full text-sm text-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-brand-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white"
          />
        </label>

        {state.status === 'checking' && <p className="text-sm text-slate-600">Checking {state.name}…</p>}

        {state.status === 'match' && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <p className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="h-5 w-5" aria-hidden="true" /> Unaltered
            </p>
            <p className="mt-1">
              This file is exactly the document &ldquo;{state.result.title}&rdquo;, completed
              {state.result.completedAt ? ` on ${formatDate(state.result.completedAt)}` : ''} with{' '}
              {state.result.signers} signer{state.result.signers === 1 ? '' : 's'}. It has not been changed since it was
              signed.
            </p>
          </div>
        )}

        {state.status === 'nomatch' && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
            <p className="flex items-center gap-2 font-semibold">
              <XCircle className="h-5 w-5" aria-hidden="true" /> No match
            </p>
            <p className="mt-1">
              This file does not match any completed document sealed by MyDocUploads. It may have been changed after
              signing, it may be a different version, or it may have been completed before fingerprints were recorded
              (November 2026).
            </p>
          </div>
        )}

        {state.status === 'error' && <p className="text-sm text-red-600">{state.message}</p>}

        {state.sha256 && (
          <p className="break-all text-xs text-slate-500">
            Fingerprint (SHA-256): <span className="font-mono">{state.sha256}</span>
          </p>
        )}
      </div>
    </PublicShell>
  );
}
